import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useRefreshKboInformation, useUser } from '~/api/user';
import { Alert, Button, Dialog, Text } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { selectBestRelativeTimeFormat } from '~/utils/dates';

import { addressStyle, addressWrapperStyle, lastUpdatedStyle } from './VerifyAddressDialog.css';

type Props = {
  onConfirm: () => void;
  onReject: () => void;
};

export const VerifyAddressDialog = ({ onConfirm, onReject }: Props) => {
  const { data: user } = useUser();
  const mutation = useRefreshKboInformation();

  const { t } = useTranslation(['settings']);
  const { formatRelativeTime } = useIntl();

  const mayRefreshKboInformation = dayjs().diff(user.kboInformationLastUpdatedAt, 'days') >= 7;

  return (
    <Dialog.Root disablePointerDismissal open>
      <Dialog.Body>
        <Dialog.Header
          description={t('settings:prompts.verifyAddress.verify.description')}
          hideCloseButton
          title={t('settings:prompts.verifyAddress.verify.title')}
        />

        <div className={addressWrapperStyle}>
          <div className={addressStyle}>
            <Text>
              {user.address.street} {user.address.number}
            </Text>

            <Text>
              {user.address.zipCode} {user.address.city}
            </Text>
          </div>

          <Text className={lastUpdatedStyle}>
            <time dateTime={user.kboInformationLastUpdatedAt}>
              {t('settings:profile.info.refreshKboInformation.lastUpdated', {
                timeAgo: formatRelativeTime(...selectBestRelativeTimeFormat(user.kboInformationLastUpdatedAt)),
              })}
            </time>
          </Text>
        </div>

        {mutation.isError && (
          <Alert announce icon="Error" status="danger">
            {t('settings:prompts.verifyAddress.verify.errors.refreshKboFailed')}
          </Alert>
        )}

        <Dialog.Actions>
          {mayRefreshKboInformation ? (
            <Button emphasis="secondary" icon="Sync" loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {t('settings:prompts.verifyAddress.verify.actions.refresh')}
            </Button>
          ) : (
            <Button emphasis="secondary" icon="Edit" onClick={onReject}>
              {t('settings:prompts.verifyAddress.verify.actions.edit')}
            </Button>
          )}

          <Button icon="Check" onClick={onConfirm}>
            {t('settings:prompts.verifyAddress.verify.actions.confirm')}
          </Button>
        </Dialog.Actions>
      </Dialog.Body>
    </Dialog.Root>
  );
};
